const findInExtraProducts = (extraProducts, id) =>
  (extraProducts || []).find((p) => p?.id?.toString() === id?.toString());

// Products configurable at their own price/route that live outside
// shootProducts entirely — suggested-products tiles (SuggestedProducts.jsx)
// and additional-offer tiles (AdditionalOffers.jsx). Both link to
// /v3/products-regular/:id and /v3/products/:id/..., so every resolver that
// accepts extraProducts should be fed this merged list rather than just
// shoot.suggestedProducts, or ids that only exist in
// shoot.additionalOfferProducts fail to resolve.
export const extraConfigProducts = (shoot) => [
  ...(shoot?.suggestedProducts || []),
  ...(shoot?.additionalOfferProducts || []),
];

// Finds a product by its own id anywhere within any category's products list
// (not just a category's parent/first product) — used by routes like
// /v3/products-regular/:id where the id may belong to any product, not just
// a category or parent product id. Falls back to extraProducts (e.g.
// state.shoot.suggestedProducts) when not found in shootProducts.
export const getProductById = (shootProducts, id, extraProducts = []) => {
  const matchingCategory = (shootProducts || []).find((category) =>
    category.products.some(
      (product) => product.id.toString() === id.toString(),
    ),
  );

  const product = matchingCategory
    ? matchingCategory.products.find((p) => p.id.toString() === id.toString())
    : findInExtraProducts(extraProducts, id);

  if (!product) throw new Error('Product not found');

  return product;
};

// Resolves the product a slot-flow route (/v3/products/:id/...) configures: a
// category's leading product, or a parent (you-pick / build-your-own) product
// anywhere in a category. Every parent tile links to the slot flow
// (getProductLink), including one that doesn't lead its category (ENG-1683).
export const getParentProductById = (shootProducts, id, extraProducts = []) => {
  const category = shootProducts.find(
    (category) => category.products[0]?.id?.toString() === id.toString(),
  );
  if (category) return category.products[0];

  const nonLeadingParent = shootProducts
    .flatMap((category) => category.products || [])
    .find(
      (product) =>
        product?.product_type === 'parent_product' &&
        product.id?.toString() === id.toString(),
    );
  if (nonLeadingParent) return nonLeadingParent;

  const extraProduct = findInExtraProducts(extraProducts, id);
  if (extraProduct) return extraProduct;
  throw new Error('Product not found');
};

export const getProductByLocation = (
  shootProducts,
  location,
  extraProducts = [],
) => {
  const path = location.pathname.split('/');
  const productId = path[path.findIndex((e) => e === 'products') + 1];

  const matchingCategory = shootProducts.find(
    (category) => category.id.toString() === productId.toString(),
  );

  if (!matchingCategory)
    return getParentProductById(shootProducts, productId, extraProducts);

  const product = matchingCategory.products.find(
    (p) => p.id.toString() === productId,
  );

  if (!product) throw new Error('Product not found');

  return product;
};

export const getChildProductById = (
  shootProducts,
  location,
  id,
  extraProducts = [],
) => {
  const path = location.pathname.split('/');
  const productId = path[path.findIndex((e) => e === 'products') + 1];

  let matchingCategory = shootProducts.find(
    (category) => category.id.toString() === productId.toString(),
  );

  const product = matchingCategory
    ? matchingCategory.products.find((p) => p.id.toString() === productId)
    : getParentProductById(shootProducts, productId, extraProducts);

  if (!product) throw new Error('Product not found');

  return product.child_products.find((p) => p.id === id);
};
